import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import toast from "react-hot-toast";
import { useGetAllCompaniesQuery } from "../toolkitServices/companyApi";
import { useAddDepartmentMutation } from "../toolkitServices/departmentApi"; // ✅ new API hook

const baseInput =
  "w-full px-3 py-2.5 border rounded-lg text-[14px] focus:outline-none";
const okRing =
  "border-gray-300 focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]";
const errRing =
  "border-red-400 focus:ring-4 focus:ring-red-200 focus:border-red-500";

const CreateDepartment = () => {
  const navigate = useNavigate();

  // Fetch companies
  const {
    data: companiesResp,
    isLoading: isCompaniesLoading,
    error: companiesError,
  } = useGetAllCompaniesQuery({ page: 1, limit: 50, search: "" });

  const companies =
    companiesResp?.data || companiesResp?.items || companiesResp?.rows || [];

  // RTK mutation
  const [addDepartment, { isLoading, isSuccess, error, data }] =
    useAddDepartmentMutation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setFocus,
  } = useForm({
    defaultValues: {
      company: "",
      departmentName: "",
      shortName: "",
    },
  });

  // ✅ Focus first field
  useEffect(() => {
    const id = requestAnimationFrame(() =>
      setFocus("departmentName", { shouldSelect: true })
    );
    return () => cancelAnimationFrame(id);
  }, [setFocus]);

  // ✅ Success handling
  useEffect(() => {
    if (isSuccess) {
      toast.success("Department created successfully!");
      reset({ company: "", departmentName: "", shortName: "" });
    }
  }, [isSuccess, reset]);

  // ❌ Error handling
  useEffect(() => {
    if (error) {
      const msg =
        error?.data?.message || error?.data?.msg || "Failed to create department!";
      toast.error(msg);
    }
  }, [error]);

  // ✅ Submit logic
  const onSubmit = async (form) => {
    const payload = {
      company: form.company,
      departmentName: form.departmentName.trim(),
      shortName: form.shortName.trim(),
    };

    try {
      await addDepartment(payload).unwrap();
    } catch {
      /* handled by toast */
    }
  };

  // ⌨️ Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      const isSave = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s";
      const isBack = (e.ctrlKey || e.metaKey) && e.key === "Backspace";
      if (isSave) {
        e.preventDefault();
        handleSubmit(onSubmit)();
      }
      if (isBack) {
        e.preventDefault();
        navigate(-1);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleSubmit, navigate, onSubmit]);

  const loading = isLoading || isSubmitting;

  return (
    <div className="px-4 py-4">
      {/* Header */}
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-2 py-2 flex items-center gap-3">
          <Link
            to="/organisation/department-master-list"
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-100 transition"
            title="Back to Department List"
          >
            <FaArrowLeft className="text-[#6f5cc5] text-sm" />
          </Link>
          <div className="flex-1">
            <h1 className="text-xl md:text-2xl font-semibold text-[#4b3d91] leading-tight">
              Create Department
            </h1>
            <p className="text-xs text-gray-500">
              Tip: Press <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">S</kbd> to Save, or{" "}
              <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">Backspace</kbd> to go back
            </p>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-4xl mx-auto bg-white shadow-md rounded-xl mt-4 border border-gray-200 relative"
      >
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-20 rounded-xl">
            <div className="animate-spin h-7 w-7 border-2 border-[#9481ce] border-t-transparent rounded-full" />
          </div>
        )}

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5"
        >
          {/* Company */}
          <div className="md:col-span-2">
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Company <span className="text-red-500">*</span>
            </label>
            <select
              {...register("company", { required: "Company is required" })}
              aria-invalid={!!errors.company}
              disabled={isCompaniesLoading || companiesError}
              className={`${baseInput} ${errors.company ? errRing : okRing}`}
            >
              <option value="">
                {isCompaniesLoading
                  ? "Loading companies…"
                  : companiesError
                  ? "Failed to load companies"
                  : "Select a company"}
              </option>
              {companies.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.companyName}
                </option>
              ))}
            </select>
            {errors.company?.message && (
              <p className="text-xs text-red-600 mt-1">{errors.company.message}</p>
            )}
          </div>

          {/* Department Name */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Department Name <span className="text-red-500">*</span>
            </label>
            <input
              {...register("departmentName", {
                required: "Department name is required",
                minLength: {
                  value: 2,
                  message: "Name must be at least 2 characters",
                },
              })}
              aria-invalid={!!errors.departmentName}
              placeholder="Enter department name"
              className={`${baseInput} ${
                errors.departmentName ? errRing : okRing
              }`}
            />
            {errors.departmentName?.message && (
              <p className="text-xs text-red-600 mt-1">
                {errors.departmentName.message}
              </p>
            )}
          </div>

          {/* Department Short Name */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Short Name <span className="text-red-500">*</span>
            </label>
            <input
              {...register("shortName", {
                required: "Short name is required",
                maxLength: {
                  value: 10,
                  message: "Max 10 characters allowed",
                },
              })}
              aria-invalid={!!errors.shortName}
              placeholder="e.g. HR, IT, SALES"
              className={`${baseInput} ${
                errors.shortName ? errRing : okRing
              } uppercase`}
            />
            {errors.shortName?.message && (
              <p className="text-xs text-red-600 mt-1">
                {errors.shortName.message}
              </p>
            )}
          </div>

          {/* Footer Buttons */}
          <div className="md:col-span-2 flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 rounded-lg bg-[#9481ce] text-white font-medium shadow-sm hover:bg-[#7d6ec0] disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save (Ctrl+S)"}
            </button>
            <Link
              to="/organisation/department-list"
              className="px-5 py-2.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-medium"
            >
              Cancel
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CreateDepartment;
